import { Link } from '@tanstack/react-router';
import { useBrand } from '../../brand/BrandProvider';
import { isItcityHost } from '../../brand/host';

/**
 * An uploaded logo, theme-aware.
 *
 * When a dark variant exists both images are rendered and CSS picks one — the theme
 * is a `dark` class on <html> (lib/theme.ts), so this needs no JS and cannot flash
 * the wrong mark on first paint the way a state-driven swap would. With no dark
 * variant the single image serves both themes.
 */
function LogoImage({ light, dark, className }: { light: string; dark: string | null; className: string }) {
  if (!dark) return <img src={light} alt="" className={className} />;
  return (
    <>
      <img src={light} alt="" className={`${className} dark:hidden`} />
      <img src={dark} alt="" className={`${className} hidden dark:block`} />
    </>
  );
}

/**
 * The two marketing brand marks, in one place because each is used by BOTH a
 * header and a footer:
 *
 *   Wordmark   — MarketingShell's header + MarketingFooter (auto variant)
 *   ITCityMark — Company's header + MarketingFooter (itcity variant)
 *
 * They live here rather than beside their pages to break an import cycle:
 * `Company` imports `MarketingFooter`, so `MarketingFooter` must not import
 * `ITCityMark` back out of `Company`.
 */

/**
 * Wordmark — brand-aware. Platform: the geometric "P" glyph + "Possignin", linked
 * to the product home. Reseller: the reseller's uploaded logo (if any) or an
 * accent square with the brand initial + the brand name, linked to the host home
 * ("/" → the reseller landing). The accent already follows the brand via
 * `--color-brand`, so no per-brand colour is needed here.
 */
export function Wordmark({ className = '' }: { className?: string }) {
  const brand = useBrand();

  // itcitysoft.com / itcity.ltd are platform hosts, but they are ITCity's own
  // domains — the shared marketing chrome there must wear the ITCity mark and
  // lead home to ITCity, not to the Possignin product page. Without this the
  // header of every /features, /pricing, /about and legal page on itcity.ltd
  // said "Possignin" and its logo navigated the visitor onto the product site.
  if (isItcityHost()) return <ITCityMark className={className} />;

  if (brand.isPlatform) {
    return (
      <Link
        to="/possignin"
        className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`}
        aria-label="Possignin home"
      >
        {/* The Possignin logo uploaded at admin → Settings → Branding (the "Possignin"
            group). This branch used to render the glyph unconditionally and never
            looked at the brand at all, so an uploaded product logo was stored and
            displayed back in the admin UI while possignin.com kept the built-in "P". */}
        {brand.logoUrl ? (
          <LogoImage
            light={brand.logoUrl}
            dark={brand.logoDarkUrl}
            className="h-8 w-auto max-w-[180px] object-contain transition-transform duration-200 group-hover:scale-[1.04]"
          />
        ) : (
          <span
            className="w-8 h-8 rounded-lg bg-brand grid place-items-center text-white shadow-sm transition-transform duration-200 group-hover:scale-[1.04] dark:bg-brand-dm dark:text-zinc-950"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 4h9a5 5 0 0 1 0 10H8v6" />
            </svg>
          </span>
        )}
        <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">
          Possignin
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/"
      className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`}
      aria-label={`${brand.name} home`}
    >
      {brand.logoUrl ? (
        <>
          <LogoImage light={brand.logoUrl} dark={brand.logoDarkUrl} className="h-8 w-auto max-w-[180px] object-contain" />
          <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </>
      ) : (
        <>
          <span
            className="w-8 h-8 rounded-lg bg-brand grid place-items-center text-white font-semibold text-[15px] shadow-sm transition-transform duration-200 group-hover:scale-[1.04] dark:bg-brand-dm dark:text-zinc-950"
            aria-hidden="true"
          >
            {(brand.shortName || brand.name).charAt(0).toUpperCase()}
          </span>
          <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </>
      )}
    </Link>
  );
}

/**
 * ITCity parent-company mark.
 *
 * Renders ITCity's OWN uploaded logo (admin → Settings → Branding, the "ITCity"
 * group), and otherwise the neutral "IT" square — deliberately not brand-accented,
 * since ITCity is the parent company rather than a product.
 *
 * The square used to be unconditional, so the ITCity site could never show a real
 * logo even after one was uploaded (reported 2026-07-30). That was then only half
 * fixed: this branch started reading `brand.logoUrl`, but the API hardcoded it to
 * null for every platform host, so an uploaded logo still never appeared anywhere.
 * `brand.logoUrl` on an ITCity host now resolves from `settings.brandingItcity` —
 * ITCity's own slot, never Possignin's. The fallback is kept because a missing logo
 * must degrade to a mark, never to an empty header.
 */
export function ITCityMark({ className = '' }: { className?: string }) {
  const brand = useBrand();
  // Only the platform brand's logo belongs on the ITCity chrome — a reseller logo
  // here would be brand leakage in the wrong direction.
  const logoUrl = brand.isPlatform ? brand.logoUrl : null;

  return (
    <Link to="/home" className={`group inline-flex items-center gap-2.5 rounded-lg ${className}`} aria-label="ITCity home">
      {logoUrl ? (
        <LogoImage
          light={logoUrl}
          dark={brand.isPlatform ? brand.logoDarkUrl : null}
          className="h-8 w-auto max-w-[180px] object-contain transition-transform duration-200 group-hover:scale-[1.04]"
        />
      ) : (
        <span
          className="w-8 h-8 rounded-lg bg-zinc-950 dark:bg-white grid place-items-center text-white dark:text-zinc-950 font-bold text-[13px] tracking-tight leading-none transition-transform duration-200 group-hover:scale-[1.04]"
          aria-hidden="true"
        >
          IT
        </span>
      )}
      <span className="font-semibold text-[17px] tracking-tight text-zinc-950 dark:text-zinc-50">ITCity</span>
    </Link>
  );
}
